import { LayerData, tLayerSettings } from '@/atoms';
import { surfaceDifferenceBodyVisibilityAtom } from '@/atoms/surfaceDifference.atom';
import { clipBoxStateAtom } from '@/atoms/viewer.atom';
import { getResourcesEndpoint } from '@/core/resources';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { applyClipBoxPlanes, buildClipBoxPlanes, CLIP_BOX_HIGHLIGHT_DIM, clipBoxIsHighlight } from '@/lib/clipBoxPlanes';
import { getLayerTransform } from '@/lib/layerTransform';
import { hasVertexDz, recolorByDz, resolveSurfaceDifferenceShading } from '@/lib/surfaceDifference/shading';
import { toOfflineUrl, useServeOffline } from '@/lib/offline/offlineResourceBase';
import { useThree } from '@react-three/fiber';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import PickableGroup from './3D/PickableGroup';

interface Entry {
  /** The run's own colormap and range, the baseline the settings override. */
  meta: { range?: [number, number] | null; colormap?: string | null } | null;
  /** The GLB carries per-vertex dz, so the settings can repaint it. */
  hasDz: boolean;
  layerId: string;
  origin: THREE.Vector3;
  scene: THREE.Group;
}

/** Disposes a single material or an array of materials (multi-material meshes). */
function disposeMaterial(material: THREE.Material | THREE.Material[] | null | undefined) {
  if (!material) return;
  if (Array.isArray(material)) material.forEach((m) => m.dispose());
  else material.dispose();
}

/**
 * Recursively disposes every mesh's geometry + material under `object`.
 * Used both when a parsed-but-now-stale scene never makes it into `entries`
 * (aborted mid-parse) and when a previous run's committed scenes are
 * replaced by a new load.
 */
function disposeScene(object: THREE.Object3D) {
  object.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry?.dispose();
    disposeMaterial(mesh.material);
  });
}

/**
 * Renders MODEL layers whose `metadata.type === 'GLB'` (currently the output
 * of SURFACE_DIFFERENCE). The GLB is authored Z-up in project coordinates
 * minus `metadata.surfaceDifference.origin`, so we position the group at that
 * origin and do NOT apply the usual glTF Y-up rotation. Child meshes are named
 * `cut` / `fill` and can be toggled via surfaceDifferenceBodyVisibilityAtom.
 */
export function GLBModel({ layers }: { layers: LayerData[] }) {
  const { projectId } = useParams();
  const serveOffline = useServeOffline();
  const layerSettings = useLayerSettingsMany(layers.map((l) => l.id));
  const clipBoxState = useAtomValue(clipBoxStateAtom(projectId!));
  const gl = useThree((s) => s.gl);
  const [entries, setEntries] = useState<Entry[]>([]);

  // Clip box → Three.js clipping planes. Unlike IFC2 (which streams new
  // materials every frame) GLB materials are fixed after load, so a memo +
  // effect per entry is enough. Planes are rebuilt only when the box changes.
  const clip = useMemo(() => buildClipBoxPlanes(clipBoxState), [clipBoxState]);
  const isHighlight = clipBoxIsHighlight(clipBoxState);
  useEffect(() => {
    if (clip) gl.localClippingEnabled = true;
  }, [clip, gl]);
  const loadKey = useMemo(() => layers.map((l) => `${l.id}:${l.root}`).join(','), [layers]);

  useEffect(() => {
    const abort = new AbortController();
    setEntries([]);
    const loader = new GLTFLoader();
    // Scenes committed to `entries` by *this* run — disposed together when
    // this run is superseded (loadKey/serveOffline change) or the component
    // unmounts. Scoping disposal to the run (rather than reacting to every
    // `entries` state change) avoids disposing a scene that's still part of
    // the currently-rendered set while a sibling layer in the same run is
    // still loading.
    const loadedScenes: THREE.Group[] = [];
    layers.forEach(async (layer) => {
      if (!layer.root) return;
      try {
        const httpsUrl = `${getResourcesEndpoint()}/${layer.root}`;
        const res = await fetch(serveOffline ? toOfflineUrl(httpsUrl) : httpsUrl, {
          signal: abort.signal,
          ...(serveOffline ? {} : { credentials: 'include' as const }),
        });
        const buf = await res.arrayBuffer();
        if (abort.signal.aborted) return;
        const gltf = await loader.parseAsync(buf, '');
        if (abort.signal.aborted) {
          // Parsed after the run was superseded/unmounted — never entered
          // `entries`, so nothing else will dispose it. Do it here.
          disposeScene(gltf.scene);
          return;
        }
        gltf.scene.traverse((o) => {
          if ((o as THREE.Mesh).isMesh) {
            const m = o as THREE.Mesh;
            disposeMaterial(m.material);
            m.material = new THREE.MeshStandardMaterial({ vertexColors: true, side: THREE.DoubleSide, flatShading: true });
          }
        });
        const sd = (layer.metadata as any)?.surfaceDifference ?? null;
        const o = (sd?.origin ?? [0, 0, 0]) as number[];
        loadedScenes.push(gltf.scene);
        setEntries((prev) => [
          ...prev,
          {
            layerId: layer.id,
            origin: new THREE.Vector3(o[0], o[1], o[2]),
            scene: gltf.scene,
            meta: sd ? { range: sd.range ?? null, colormap: sd.colormap ?? null } : null,
            hasDz: hasVertexDz(gltf.scene),
          },
        ]);
      } catch (err) {
        if (!abort.signal.aborted) console.error('Error loading GLB layer:', err);
      }
    });
    return () => {
      abort.abort();
      for (const scene of loadedScenes) disposeScene(scene);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey, serveOffline]);

  return (
    <>
      {entries.map((e) => (
        <GLBEntry key={e.layerId} entry={e} settings={layerSettings[e.layerId]} clip={clip} isHighlight={isHighlight} />
      ))}
    </>
  );
}

function GLBEntry({
  entry,
  settings,
  clip,
  isHighlight,
}: {
  entry: Entry;
  settings: tLayerSettings | null | undefined;
  clip: ReturnType<typeof buildClipBoxPlanes>;
  isHighlight: boolean;
}) {
  const vis = useAtomValue(surfaceDifferenceBodyVisibilityAtom(entry.layerId));
  useEffect(() => {
    entry.scene.traverse((o) => {
      if (o.name === 'cut') o.visible = vis.cut;
      if (o.name === 'fill') o.visible = vis.fill;
    });
  }, [entry, vis]);

  // Recolour from the layer settings (colormap + display range) whenever
  // they change. Only a GLB that carries per-vertex dz can be repainted; an
  // older one keeps the colours the worker baked. Runs on the CPU once per
  // change, never per frame — the same approach as LandXML's surface shading.
  const colormap = settings?.surfaceDifferenceColormap ?? null;
  const rangeMin = settings?.scalarRangeMin ?? null;
  const rangeMax = settings?.scalarRangeMax ?? null;
  useEffect(() => {
    if (!entry.hasDz) return;
    const { gradient, min, max } = resolveSurfaceDifferenceShading(
      { surfaceDifferenceColormap: colormap, scalarRangeMin: rangeMin, scalarRangeMax: rangeMax },
      entry.meta,
    );
    entry.scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh) recolorByDz(mesh.geometry as THREE.BufferGeometry, gradient, min, max);
    });
  }, [entry, colormap, rangeMin, rangeMax]);

  // CLIP_OUTSIDE / CLIP_INSIDE: hardware clipping planes on every material.
  useEffect(() => {
    applyClipBoxPlanes(entry.scene, clip);
  }, [entry, clip]);

  // HIGHLIGHT_INSIDE: dim the whole model (the orange box overlay itself is
  // drawn once by IFC2, which is always mounted alongside this component).
  const baseOpacity = settings?.modelOpacity ?? 1.0;
  useEffect(() => {
    const opacity = baseOpacity * (isHighlight ? CLIP_BOX_HIGHLIGHT_DIM : 1.0);
    entry.scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of mats) {
        if (!mat) continue;
        const transparent = opacity < 1;
        if (mat.transparent !== transparent) {
          mat.transparent = transparent;
          mat.needsUpdate = true;
        }
        mat.opacity = opacity;
        mat.depthWrite = !transparent;
      }
    });
  }, [entry, baseOpacity, isHighlight]);
  if (!settings) return null;
  const transform = getLayerTransform(settings);
  const position = transform.hasOffset ? entry.origin.clone().add(transform.offsetVec) : entry.origin;
  return (
    <PickableGroup position={position} rotation={transform.rotation}>
      <primitive object={entry.scene} />
    </PickableGroup>
  );
}
